import { BrainCircuit, Cpu, GitBranch, Pause, Play, Plus, Settings2 } from "lucide-react";
import { useNeural } from "@/store/neuralStore";
import { ModelMultiSelect } from "./ModelMultiSelect";
import { toast } from "sonner";

export function AgentsHub() {
  const agents = useNeural((s) => s.agents);
  const setAgentModels = useNeural((s) => s.setAgentModels);
  const toggleEnsemble = useNeural((s) => s.toggleEnsemble);
  const toggleStatus = useNeural((s) => s.toggleAgentStatus);
  const addAgent = useNeural((s) => s.addAgent);

  return (
    <div className="h-full overflow-y-auto p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <BrainCircuit className="w-6 h-6 text-primary" /> Agents Hub
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            {agents.length} agents · multi-model per agent · ensemble voting · auto-scaling
          </p>
        </div>
        <button
          onClick={() => {
            addAgent({
              name: `Agent-${Math.floor(Math.random() * 999)}`,
              role: "Custom worker",
              models: ["gemini-3-flash"],
              ensemble: false,
              status: "active",
              calls: 0,
              success: 100,
              x: Math.random(),
              y: Math.random(),
              color: `hsl(${Math.floor(Math.random() * 360)} 90% 60%)`,
              emoji: "🤖",
            });
            toast.success("New agent forged");
          }}
          className="text-sm px-4 py-2 rounded-md bg-gradient-neural text-primary-foreground border-0 shadow-glow-cyan flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> Forge New Agent
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {agents.map((a) => (
          <div key={a.id} className="glass rounded-xl p-4 hover:border-primary/40 transition-all relative overflow-visible">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-lg"
                  style={{ background: `linear-gradient(135deg, ${a.color}, hsl(270 90% 65%))`, boxShadow: `0 0 16px ${a.color}` }}
                >
                  {a.emoji}
                </div>
                <div>
                  <div className="font-display font-semibold text-sm">{a.name}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">{a.role}</div>
                </div>
              </div>
              <span
                className={`status-dot ${
                  a.status === "active" ? "text-success animate-pulse" : a.status === "paused" ? "text-warning" : "text-muted-foreground"
                }`}
              />
            </div>

            <div className="flex items-center gap-1.5 mb-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
              <Cpu className="w-3 h-3 text-secondary" /> Models · ensemble per-agent
            </div>
            <div className="relative mb-3">
              <ModelMultiSelect
                selected={a.models}
                onChange={(next) => setAgentModels(a.id, next)}
                ensemble={a.ensemble}
                onToggleEnsemble={() => toggleEnsemble(a.id)}
                compact
              />
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rounded-md bg-surface-2/60 p-2 border border-border">
                <div className="text-[10px] font-mono uppercase text-muted-foreground">Calls</div>
                <div className="text-sm font-mono">{a.calls.toLocaleString()}</div>
              </div>
              <div className="rounded-md bg-surface-2/60 p-2 border border-border">
                <div className="text-[10px] font-mono uppercase text-muted-foreground">Success</div>
                <div className="text-sm font-mono text-success">{a.success}%</div>
              </div>
            </div>

            <div className="flex gap-1.5">
              <button
                onClick={() => toggleStatus(a.id)}
                className="flex-1 text-[11px] font-mono py-1.5 rounded border border-border hover:border-primary/40 hover:text-primary flex items-center justify-center gap-1"
              >
                {a.status === "active" ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                {a.status === "active" ? "Pause" : "Resume"}
              </button>
              <button className="flex-1 text-[11px] font-mono py-1.5 rounded border border-border hover:border-secondary/40 hover:text-secondary flex items-center justify-center gap-1">
                <GitBranch className="w-3 h-3" /> Fork
              </button>
              <button className="text-[11px] font-mono py-1.5 px-2 rounded border border-border hover:border-accent/40 hover:text-accent">
                <Settings2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
